/**
 * 组织管理 API (租户端 - ERP Service)
 * 对应 erp-service (7091)
 */

import { get, post, put, del } from '@/utils/request';

// ==================== 类型定义 ====================

/** 组织关系类型 */
export type RelationType =
  | 'headquarters'   // 总部
  | 'subsidiary'     // 子公司
  | 'branch'         // 分公司
  | 'division'       // 事业部
  | 'region'         // 区域
  | 'store'          // 门店
  | 'direct_store'   // 直营店
  | 'franchise'      // 加盟店
  | 'dealer'         // 经销商
  | 'distributor'    // 分销商
  | 'agent'          // 代理商
  | 'ecommerce'      // 电商渠道
  | 'supplier'       // 供应商
  | 'manufacturer'   // 制造商
  | 'logistics'      // 物流商
  | 'warehouse'      // 仓库
  | 'customer'       // 客户
  | 'partner';       // 合作伙伴

/** 组织状态 */
export type OrganizationStatus = 'active' | 'inactive' | 'archived';

/** 组织信息 */
export interface Organization {
  id: number;
  tenant_id: number;
  parent_id: number | null;
  name: string;
  code: string;
  short_name: string;
  path: string;
  level: number;
  sort_order: number;
  relation_types: RelationType[];
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  province: string;
  city: string;
  district: string;
  address: string;
  credit_code: string;
  legal_person: string;
  bank_name: string;
  bank_account: string;
  valid_from: string | null;
  valid_to: string | null;
  logo_url: string;
  remark: string;
  status: OrganizationStatus;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by: number;
  children?: Organization[];
}

/** 用户的组织选项（用于切换下拉） */
export interface MyOrganization {
  id: number;
  name: string;
  short_name: string;
  code: string;
  relation_types: RelationType[];
  is_primary: boolean;
  is_current: boolean;
  role: string;
}

/** 组织树节点 */
export interface OrganizationTreeNode {
  id: number;
  parent_id: number | null;
  name: string;
  code: string;
  short_name: string;
  level: number;
  path: string;
  relation_types: RelationType[];
  status: OrganizationStatus;
  children_count: number;
  children?: OrganizationTreeNode[];
}

/** 创建组织请求 */
export interface CreateOrganizationRequest {
  parent_id?: number;
  name: string;
  code?: string;
  short_name?: string;
  relation_types: RelationType[];
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  province?: string;
  city?: string;
  district?: string;
  address?: string;
  credit_code?: string;
  legal_person?: string;
  bank_name?: string;
  bank_account?: string;
  valid_from?: string;
  valid_to?: string;
  logo_url?: string;
  remark?: string;
  sort_order?: number;
}

/** 更新组织请求 */
export interface UpdateOrganizationRequest {
  name?: string;
  short_name?: string;
  relation_types?: RelationType[];
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  province?: string;
  city?: string;
  district?: string;
  address?: string;
  credit_code?: string;
  legal_person?: string;
  bank_name?: string;
  bank_account?: string;
  valid_from?: string;
  valid_to?: string;
  logo_url?: string;
  remark?: string;
  sort_order?: number;
  status?: OrganizationStatus;
}

/** 组织列表查询参数 */
export interface OrganizationListParams {
  parent_id?: number;
  status?: OrganizationStatus;
  relation_types?: RelationType[];
  keyword?: string;
  page?: number;
  page_size?: number;
}

/** 切换组织响应 */
export interface SwitchOrgResponse {
  message: string;
  token: string;
  current_org: Organization;
}

/** 合并组织请求 */
export interface MergeOrganizationRequest {
  source_org_id: number;  // 被合并的组织（将被删除）
  target_org_id: number;  // 目标组织（保留）
  merge_users: boolean;   // 是否迁移用户
}

/** 拆分组织请求 */
export interface SplitOrganizationRequest {
  source_org_id: number;       // 被拆分的组织
  new_org_name: string;        // 新组织名称
  new_org_code?: string;       // 新组织编码
  relation_types?: RelationType[];  // 新组织关系类型
  child_org_ids?: number[];    // 要迁移到新组织的子组织ID
  user_ids?: number[];         // 要迁移到新组织的用户ID
  same_parent?: boolean;       // 新组织是否和源组织同级(true)还是作为源组织子级(false)
}

/** 合并组织响应 */
export interface MergeOrganizationResponse {
  message: string;
  target_org: Organization;
}

/** 拆分组织响应 */
export interface SplitOrganizationResponse {
  message: string;
  new_org: Organization;
}

// ==================== API 函数 ====================

/** 获取组织列表 */
export const getOrganizationList = (params?: OrganizationListParams) =>
  get<{ items: Organization[]; total: number }>('/api/v1/organizations', params);

/** 获取组织树 */
export const getOrganizationTree = async (): Promise<OrganizationTreeNode[]> => {
  const response = await get<{ tree: OrganizationTreeNode[] } | OrganizationTreeNode[]>('/api/v1/organizations/tree');
  // 兼容两种响应格式：{tree: [...]} 或直接返回数组
  if (response && typeof response === 'object' && 'tree' in response) {
    return response.tree;
  }
  return Array.isArray(response) ? response : [];
};

/** 获取组织结构图数据 */
export const getOrganizationChart = () =>
  get<OrganizationTreeNode[]>('/api/v1/organizations/chart');

/** 获取我的组织列表（用于组织切换） */
export const getMyOrganizations = () =>
  get<{ items: MyOrganization[]; total: number }>('/api/v1/organizations/my');

/** 获取当前组织 */
export const getCurrentOrganization = () =>
  get<Organization>('/api/v1/organizations/current');

/** 切换当前组织 */
export const switchOrganization = (orgId: number) =>
  post<SwitchOrgResponse>('/api/v1/organizations/switch', { org_id: orgId });

/** 获取组织详情 */
export const getOrganization = (id: number) =>
  get<Organization>(`/api/v1/organizations/${id}`);

/** 创建组织 */
export const createOrganization = (data: CreateOrganizationRequest) =>
  post<Organization>('/api/v1/organizations', data);

/** 更新组织 */
export const updateOrganization = (id: number, data: UpdateOrganizationRequest) =>
  put<Organization>(`/api/v1/organizations/${id}`, data);

/** 删除组织 */
export const deleteOrganization = (id: number) =>
  del<void>(`/api/v1/organizations/${id}`);

/** 迁移组织（修改父级） */
export const moveOrganization = (id: number, newParentId?: number) =>
  post<void>(`/api/v1/organizations/${id}/move`, { new_parent_id: newParentId });

/** 获取组织下的用户列表 */
export const getOrganizationUsers = (orgId: number) =>
  get<{ items: any[]; total: number }>(`/api/v1/organizations/${orgId}/users`);

/** 添加用户到组织 */
export const addUserToOrganization = (orgId: number, userId: number, isPrimary?: boolean, role?: string) =>
  post<void>(`/api/v1/organizations/${orgId}/users`, { user_id: userId, is_primary: isPrimary, role });

/** 从组织移除用户 */
export const removeUserFromOrganization = (orgId: number, userId: number) =>
  del<void>(`/api/v1/organizations/${orgId}/users/${userId}`);

/** 合并组织 */
export const mergeOrganization = (data: MergeOrganizationRequest) =>
  post<MergeOrganizationResponse>('/api/v1/organizations/merge', data);

/** 拆分组织 */
export const splitOrganization = (data: SplitOrganizationRequest) =>
  post<SplitOrganizationResponse>('/api/v1/organizations/split', data);

// ==================== 辅助函数 ====================

/** 关系类型标签映射 */
export const relationTypeLabels: Record<RelationType, string> = {
  headquarters: '总部',
  subsidiary: '子公司',
  branch: '分公司',
  division: '事业部',
  region: '区域',
  store: '门店',
  direct_store: '直营店',
  franchise: '加盟店',
  dealer: '经销商',
  distributor: '分销商',
  agent: '代理商',
  ecommerce: '电商渠道',
  supplier: '供应商',
  manufacturer: '制造商',
  logistics: '物流商',
  warehouse: '仓库',
  customer: '客户',
  partner: '合作伙伴',
};

/** 获取关系类型标签 */
export const getRelationTypeLabel = (type: RelationType): string => {
  return relationTypeLabels[type] || type;
};

/** 获取组织状态标签 */
export const getStatusLabel = (status: OrganizationStatus): string => {
  const labels: Record<OrganizationStatus, string> = {
    active: '启用',
    inactive: '禁用',
    archived: '已归档',
  };
  return labels[status] || status;
};

/** 组织 API 对象 */
export const organizationApi = {
  getOrganizationList,
  getOrganizationTree,
  getOrganizationChart,
  getMyOrganizations,
  getCurrentOrganization,
  switchOrganization,
  getOrganization,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  moveOrganization,
  mergeOrganization,
  splitOrganization,
  getOrganizationUsers,
  addUserToOrganization,
  removeUserFromOrganization,
};

export default organizationApi;
